"use client";
import React from "react";
import BreadCrumbs from "@/components/BreadCrumbs/BreadCrumbs";
import AboutPackage from "@/components/About/About";
import ConsultationForm from "./ConsultationForm";
import FAQ from "./FAQ/FAQ";
import ForRating from "./Rating/ForRating";
import Image from "next/image";

const ExpertDetails = ({ data }) => {
  if (!data) {
    return <p>No expert details available.</p>;
  }

  // Destructure or define defaults
  const {
    img = {},
    About,
    name,
    rating,
    level,
    designation,
    experience,
    classes,
    location,
    awards_and_achievements,
    courses,
    treatment_packages,
    faqs,
  } = data;

  return (
    <div className="mx-auto w-full px-4 sm:px-6 lg:px-8 max-w-[1440px]">
      <div className="w-full mt-6">
        <BreadCrumbs />
      </div>
      <main className="flex flex-col md:flex-row gap-6 lg:gap-8 w-full mt-6">
        {/* Left column */}
        <div className="flex-1 flex flex-col gap-5">
          <div className="w-full max-w-[900px]">
            <AboutPackage
              img={img?.url || ""}
              info={About || "No additional information available."}
              name={name || "No Name"}
            />
          </div>

          {/* Expert Info Section */}
          <div className="expertInfo px-0 md:ml-4">
            <div className="-ml-1 mb-2">
              {typeof rating === "number" && (
                <ForRating
                  initialValue={rating}
                  allowFraction={true}
                  size={20}
                  readonly
                />
              )}
            </div>
            {level && <p className="level text-center sm:text-left">{level}</p>}
            {designation && (
              <p className="designation text-center sm:text-left">{designation}</p>
            )}
            {typeof experience !== "undefined" && (
              <p className="value text-center sm:text-left">
                <span className="highlight">Experience:</span> {experience} years
              </p>
            )}
            {typeof classes !== "undefined" && (
              <p className="value text-center sm:text-left">
                <span className="highlight">Classes:</span> {classes}
              </p>
            )}
            {location && (
              <p className="value text-center sm:text-left">
                <span className="highlight">Location:</span> {location}
              </p>
            )}
            {awards_and_achievements &&
              Array.isArray(awards_and_achievements) &&
              awards_and_achievements.length > 0 && (
              <div className="awards mt-5">
                <h3 className="text-lg font-semibold">Awards and Achievements:</h3>
                <p>
                  {awards_and_achievements.map((award, idx) => (
                    <span key={idx}>
                      {award?.name || "N/A"}
                      {idx !== awards_and_achievements.length - 1 && ", "}
                    </span>
                  ))}
                </p>
              </div>
            )}
          </div>

          {/* Courses Section */}
          {courses && Array.isArray(courses) && courses.length > 0 && (
            <div className="courses w-full mt-8 max-w-[900px]">
              <h2 className="text-2xl font-bold mb-5">Courses</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                {courses.map((course) => (
                  <div
                    key={course?.id || course?.name}
                    className="course-card p-5 border rounded-lg shadow-md"
                  >
                    {course?.image?.url ? (
                      <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-gray-100">
                        <Image
                          src={course.image.url}
                          alt={course.name || "Course Image"}
                          fill
                          priority={false}
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          style={{ objectFit: "cover" }}
                        />
                      </div>
                    ) : (
                      <div className="w-full aspect-square bg-gray-200 flex items-center justify-center rounded-lg">
                        Image not available
                      </div>
                    )}
                    <h3 className="text-xl font-semibold mt-3">
                      {course?.name || "No Name"}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Treatment Packages Section */}
          {treatment_packages &&
            Array.isArray(treatment_packages) &&
            treatment_packages.length > 0 && (
            <div className="treatment-packages w-full mt-8 max-w-[900px]">
              <h2 className="text-2xl font-bold mb-5">Treatment Packages</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {treatment_packages.map((pkg) => (
                  <div
                    key={pkg?.id || pkg?.packageName}
                    className="package-card p-5 border rounded-lg shadow-md"
                  >
                    <h3 className="text-xl font-semibold mb-3">
                      {pkg?.packageName || "Package Name"}
                    </h3>
                    {pkg?.duration && (
                      <p>
                        <span className="highlight">Duration:</span> {pkg.duration}
                      </p>
                    )}
                    {pkg?.hospitalName && (
                      <p>
                        <span className="highlight">Hospital:</span> {pkg.hospitalName}
                      </p>
                    )}
                    {(pkg?.country_from || pkg?.country_to) && (
                      <p>
                        {pkg?.country_from && (
                          <span className="highlight">From:</span>
                        )}{" "}
                        {pkg?.country_from || "N/A"}{" "}
                        {pkg?.country_to && (
                          <>
                            <span className="highlight"> To:</span> {pkg?.country_to}
                          </>
                        )}
                      </p>
                    )}
                    {typeof pkg?.packageCost !== "undefined" && (
                      <p>
                        <span className="highlight">Cost:</span> ${pkg.packageCost}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FAQs Section */}
          {faqs && Array.isArray(faqs) && faqs.length > 0 && <FAQ data={faqs} />}
        </div>

        {/* Right column: Consultation Form */}
        <div className="w-full md:w-[380px] lg:w-[420px] md:sticky md:top-6 h-fit">
          <ConsultationForm />
        </div>
      </main>
    </div>
  );
};

export default ExpertDetails;
